import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api, { mediaUrl } from '../api/client';
import Seo from '../components/Seo';
import Breadcrumb from '../components/common/Breadcrumb';
import Pagination from '../components/common/Pagination';
import Spinner from '../components/common/Spinner';
import { useSettings } from '../context/SettingsContext';
import { formatDate } from '../utils/format';

// Blog index replicating the reference "news-hub" layout (sidebar categories + article rows).
export default function Blog() {
  const { settings } = useSettings();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const page = Number(params.get('page') || 1);
  const category = params.get('category') || '';
  const blogSlug = settings.pages?.blogSlug || 'news';

  useEffect(() => {
    setLoading(true);
    api.get('/blogs', { params: { page, limit: 10, category: category || undefined } }).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [page, category]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k !== 'page') next.delete('page');
    setParams(next);
  };
  const title = settings.pages?.blogTitle || 'NEWS';

  return (
    <>
      <Seo title={category ? `${category} – ${title}` : title} description={settings.pages?.blogSubtitle || `Latest news, guides and stories from ${settings.general?.siteName}.`} type="blog" breadcrumbs={[{ label: 'Home', url: '/' }, { label: title }]} />
      <section className="news-hub">
        <div className="news-hub__hero"><h1>{title}</h1>{settings.pages?.blogSubtitle && <p className="text-color-secondary mt-2">{settings.pages.blogSubtitle}</p>}</div>
        <div className="container-fluid">
          <div className="news-hub__breadcrumb"><Breadcrumb items={[{ label: title.toLowerCase() }]} /></div>
          <div className="news-hub__container">
            <div className="news-hub__layout">
              <aside className="news-hub__sidebar">
                <div className="news-hub__sidebar-head"><h2 className="news-hub__sidebar-title">Category</h2></div>
                <nav className="news-hub__sidebar-nav">
                  <ul className="news-hub__sidebar-list">
                    <li className="news-hub__sidebar-item">
                      <button type="button" className={`news-hub__sidebar-link ${!category ? 'is-active' : ''}`} onClick={() => setParam('category', '')}>
                        <span className="news-hub__sidebar-link-main"><span className="news-hub__sidebar-chevron">›</span><span>All posts</span></span>
                        <span className="news-hub__sidebar-count">{data?.pagination?.total ?? ''}</span>
                      </button>
                    </li>
                    {(data?.categories || []).map((c) => (
                      <li className="news-hub__sidebar-item" key={c.name}>
                        <button type="button" className={`news-hub__sidebar-link ${category === c.name ? 'is-active' : ''}`} onClick={() => setParam('category', c.name)}>
                          <span className="news-hub__sidebar-link-main"><span className="news-hub__sidebar-chevron">›</span><span>{c.name}</span></span>
                          <span className="news-hub__sidebar-count">{c.count}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </aside>
              <div className="news-hub__main">
                <section className="news-hub__list">
                  {loading ? (
                    <Spinner />
                  ) : (
                    <div className="news-hub__article-list">
                      {(data?.items || []).map((post) => (
                        <article className="news-hub__article-row" key={post._id}>
                          <Link className="news-hub__article-media" to={`/blogs/${blogSlug}/${post.slug}`}>
                            {post.image && <img className="news-hub__article-img" src={mediaUrl(post.image)} alt={post.title} loading="lazy" />}
                          </Link>
                          <div className="news-hub__article-body">
                            <time className="news-hub__article-date" dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
                            <h3 className="news-hub__article-title"><Link to={`/blogs/${blogSlug}/${post.slug}`}>{post.title}</Link></h3>
                            <div className="news-hub__article-excerpt">{post.excerpt}</div>
                            <Link className="news-hub__article-link" to={`/blogs/${blogSlug}/${post.slug}`}>Read more</Link>
                          </div>
                        </article>
                      ))}
                      {data && data.items.length === 0 && <p className="text-color-secondary py-10">No posts yet.</p>}
                    </div>
                  )}
                </section>
                <div className="news-hub__pagination">
                  <Pagination page={data?.pagination?.page || 1} pages={data?.pagination?.pages || 1} onChange={(n) => setParam('page', String(n))} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
